import c from 'classnames';
import { graphqlApiSite } from 'core/api/graphql';
import CenterLoader from 'core/components/common/CenterLoader';
import { showSnackbar } from 'core/components/snackbar';
import Tab from 'core/components/Tab';
import getMyAccount, { IGetMyAccountState } from 'core/enhancers/getMyAccount';
import showAllEnhacer, { IShowAllEnhacerProps } from 'core/enhancers/showAllEnhacer';
import { button, buttonAccent, fieldInput } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatToLocalDayMonth, isMobile } from 'core/utils';
import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import compose from 'recompose/compose';
import { ReferralAccountInfo, ReferralAccountInfoPerDay } from 'site/graphql';
import smoothScroll from 'smooth-scroll';

type IProfileReferralProps = IShowAllEnhacerProps & IGetMyAccountState & WithStyles<typeof styles>;

type IProfileReferralState = {
    tab: 'none' | 'common' | 'detail' | 'info';
    isFetching: boolean;
    link: string;
    referralAccountInfo: ReferralAccountInfo;
    referralAccountInfoPerDay: ReferralAccountInfoPerDay[];
};

class ProfileReferral extends React.Component<IProfileReferralProps, IProfileReferralState> {
    container: HTMLDivElement;

    state: IProfileReferralState = {
        tab: 'info',
        isFetching: false,
        link: 'https://red-circule.com',
        referralAccountInfo: null,
        referralAccountInfoPerDay: [],
    };

    handleChangeLink = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ link: event.target.value });
    handleChangeTab = (tab: IProfileReferralState['tab']) => {
        const scroll = new smoothScroll();
        const newTab = isMobile() ? (this.state.tab === tab ? 'none' : tab) : tab;

        this.setState({ tab: newTab });
        isMobile() && scroll.animateScroll(this.container);
    };

    componentDidMount() {
        this.setState({ isFetching: true });

        const query = `{
            referralAccountInfo {
                refAccountsCount
                paymentsCount
                totalAccrued
                totalWrittenOff
                balance
            }
            referralAccountInfoPerDay {
                date
                refAccountsCount
                totalAccrued
                totalWriteOff
            }
        }`;

        graphqlApiSite<{
            referralAccountInfo: ReferralAccountInfo;
            referralAccountInfoPerDay: ReferralAccountInfoPerDay[];
        }>(query, null)
            .then(({ data }) =>
                this.setState({
                    isFetching: false,
                    referralAccountInfo: data.referralAccountInfo,
                    referralAccountInfoPerDay: data.referralAccountInfoPerDay,
                }),
            )
            .catch(() => this.setState({ isFetching: false }));
    }

    renderCommonTable() {
        const { classes } = this.props;
        const { referralAccountInfo } = this.state;

        return (
            referralAccountInfo && (
                <div className={classes.tableCommonWrapper}>
                    <table className={c(classes.table, classes.tableCommon)}>
                        <tbody>
                            <tr>
                                <td className={c(classes.tableTd, classes.boldText, classes.textAlignLeft)}>
                                    Закрепилось рефералов
                                </td>
                                <td className={classes.tableTd}>{referralAccountInfo.refAccountsCount}</td>
                            </tr>
                            <tr>
                                <td className={c(classes.tableTd, classes.boldText, classes.textAlignLeft)}>
                                    Кол-во оплат рефералов
                                </td>
                                <td className={classes.tableTd}>{referralAccountInfo.paymentsCount}</td>
                            </tr>
                            <tr>
                                <td className={c(classes.tableTd, classes.boldText, classes.textAlignLeft)}>
                                    Начислено баллов
                                </td>
                                <td className={classes.tableTd}>{referralAccountInfo.totalAccrued}</td>
                            </tr>
                            <tr>
                                <td className={c(classes.tableTd, classes.boldText, classes.textAlignLeft)}>
                                    Списано баллов
                                </td>
                                <td className={classes.tableTd}>{referralAccountInfo.totalWrittenOff}</td>
                            </tr>
                            <tr>
                                <td className={c(classes.tableTd, classes.boldText, classes.textAlignLeft)}>
                                    Осталось баллов
                                </td>
                                <td className={classes.tableTd}>{referralAccountInfo.balance}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )
        );
    }

    renderDetailTable() {
        const { classes, showAll, onShowAllClick } = this.props;
        const { referralAccountInfoPerDay } = this.state;
        const data = showAll ? referralAccountInfoPerDay : referralAccountInfoPerDay.slice(0, 10);

        return (
            data.length > 0 && (
                <React.Fragment>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th className={c(classes.tableTd, classes.tableTh)}>Дата</th>
                                <th className={c(classes.tableTd, classes.tableTh)}>Начисления</th>
                                <th className={c(classes.tableTd, classes.tableTh)}>Списания</th>
                                <th className={c(classes.tableTd, classes.tableTh)}>Кол-во рефералов</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(day => (
                                <tr key={day.date}>
                                    <td className={classes.tableTd}>{formatToLocalDayMonth(day.date)}</td>
                                    <td className={classes.tableTd}>{day.totalAccrued}</td>
                                    <td className={classes.tableTd}>{day.totalWriteOff}</td>
                                    <td className={classes.tableTd}>{day.refAccountsCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!showAll && (
                        <div onClick={onShowAllClick} className={c(classes.showAllButton, classes.marginBottom)}>
                            Показать все
                        </div>
                    )}
                </React.Fragment>
            )
        );
    }

    renderInfo() {
        const { classes, account } = this.props;
        const { link } = this.state;
        const resultLink = `${link}?ref=${account.refLink}`;

        return (
            <React.Fragment>
                <div className={c(classes.boldText, classes.marginBottom)}>
                    Реферальная программа позволит вам стать партнером и зарабатывать на нашей платформе!
                </div>
                <div className={classes.marginBottom}>
                    Вставьте в поле ссылку на любую страницу сайта для генерации вашей реферальной ссылки
                </div>
                <input
                    className={c(classes.input, classes.marginBottom)}
                    value={link}
                    onChange={this.handleChangeLink}
                />
                <div className={c(classes.boldText, classes.marginBottom)}>
                    Ваша реферальная ссылка:{' '}
                    <a href={resultLink} className={classes.referrLink} target="_blank" rel="noopener noreferrer">
                        {resultLink}
                    </a>
                </div>
                <CopyToClipboard
                    text={resultLink}
                    onCopy={() => showSnackbar({ snackbarMessage: 'Ссылка скопирована' })}
                >
                    <button className={c(classes.copyToClipboardButton, classes.marginBottom)}>
                        Копировать ссылку
                    </button>
                </CopyToClipboard>
                <div>Как работает программа: </div>
                <ol>
                    <li>Вы генерируете свою ссылку</li>
                    <li>Распространяете ссылку</li>
                    <li>
                        За каждую покупку нового пользователя, зарегистрировавшегося на нашей платформе, вы получаете
                        бонусы в виде баллов
                    </li>
                </ol>
            </React.Fragment>
        );
    }

    render() {
        const { classes } = this.props;
        const { isFetching, tab } = this.state;

        if (isFetching) {
            return <CenterLoader />;
        }

        return (
            <div ref={div => (this.container = div)}>
                {isMobile() ? (
                    <React.Fragment>
                        <Tab currentTab={tab} label="Получить ссылку" onSelect={this.handleChangeTab} tabName="info" />
                        {tab === 'info' && this.renderInfo()}
                        <Tab
                            currentTab={tab}
                            label="Общая информация"
                            onSelect={this.handleChangeTab}
                            tabName="common"
                        />
                        {tab === 'common' && this.renderCommonTable()}
                        <Tab
                            currentTab={tab}
                            label="Детализация по дням"
                            onSelect={this.handleChangeTab}
                            tabName="detail"
                        />
                        {tab === 'detail' && this.renderDetailTable()}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <div className={classes.tabsContainer}>
                            <Tab
                                currentTab={tab}
                                label="Получить ссылку"
                                onSelect={this.handleChangeTab}
                                tabName="info"
                            />
                            <Tab
                                currentTab={tab}
                                label="Общая информация"
                                onSelect={this.handleChangeTab}
                                tabName="common"
                            />
                            <Tab
                                currentTab={tab}
                                label="Детализация по дням"
                                onSelect={this.handleChangeTab}
                                tabName="detail"
                            />
                        </div>
                        {tab === 'info' && this.renderInfo()}
                        {tab === 'common' && this.renderCommonTable()}
                        {tab === 'detail' && this.renderDetailTable()}
                    </React.Fragment>
                )}
            </div>
        );
    }
}

const styles = (theme: ITheme) =>
    createStyles({
        table: {
            borderCollapse: 'collapse',
            width: 100 + '%',
            marginBottom: 1 + 'rem',

            [theme.breakpoints.down('xs')]: {
                fontSize: 0.8 + 'rem',
                display: 'block',
                overflowX: 'auto',
            },
        },
        tableCommon: {
            maxWidth: 700,
        },
        tableCommonWrapper: {
            display: 'flex',
            justifyContent: 'center',
        },
        tableTd: {
            padding: '4px 10px',
            textAlign: 'center',
            border: '1px solid #c0c0c0',
            fontSize: 0.9 + 'rem',
            minWidth: 100,
        },
        tableTh: {
            backgroundColor: theme.colors.contrastBg,
            color: theme.colors.contrastBgText,
            fontWeight: 400,
            padding: 8,
        },
        input: {
            ...fieldInput,
            maxWidth: 300,
            width: 100 + '%',

            [theme.breakpoints.down('xs')]: {
                width: 'calc(100% - 28px)',
            },
        },
        marginBottom: {
            marginBottom: 1 + 'rem',
        },
        boldText: {
            fontWeight: 'bold',
        },
        textAlignLeft: {
            textAlign: 'left',
        },
        tabsContainer: {
            display: 'flex',
            marginBottom: 1 + 'rem',
        },
        showAllButton: {
            fontWeight: 'bold',
            borderBottom: `2px solid ${theme.colors.black}`,
            alignSelf: 'end',
            cursor: 'pointer',
            fontSize: 0.9 + 'rem',
            display: 'inline-block',
        },
        copyToClipboardButton: {
            ...button(theme),
            ...buttonAccent(theme),
            width: 100 + '%',
            maxWidth: 250,
            padding: 13,

            '&:focus': {
                outline: 'none',
            },

            [theme.breakpoints.down('xs')]: {
                maxWidth: 'none',
            },
        },
        referrLink: {
            textDecoration: 'none',
        },
    });

export default compose(getMyAccount, showAllEnhacer, injectSheet(styles, 'AccountCertificates'))(ProfileReferral);
